import { useState } from "react";

import { cn } from "@/lib/utils";
import type {
  InternalBeforeAfter,
  InternalDetailData,
  InternalMetricItem,
  InternalMetrics,
  InternalOverview,
  InternalTech,
} from "@/services/navigator/internal-detail-types";

import { InternalSection, InternalTag } from "./internal-parts";

// 개요 · 도입 전후 · 차별화 포인트 · 핵심 AI 기술 · 목표(검증 · 성과) 지표 · 보안과 신뢰 (Figma ver.7 03-09~12)

// ── 개요 ──
export function OverviewSection({ data }: { data: InternalOverview }) {
  const hasCard = !!(data.domain || data.department);
  return (
    <InternalSection id="overview" title="개요">
      <div className={cn("grid gap-6 lg:gap-12", hasCard ? "lg:grid-cols-[minmax(0,1fr)_340px]" : "grid-cols-1")}>
        <div className="flex min-w-0 flex-col">
          {data.task && (
            <>
              <span className="text-[13px] leading-[21px] text-nv-text-tertiary">적용 업무</span>
              <p className="mt-[14px] text-[24px] font-normal leading-[34px] text-nv-text-primary xl:text-[30px] xl:leading-[42px]">{data.task}</p>
            </>
          )}
          {data.text && <p className={cn("whitespace-pre-line text-[16px] leading-[27px] text-nv-text-secondary", data.task && "mt-[14px]")}>{data.text}</p>}
        </div>
        {hasCard && (
          <div className="flex flex-col gap-9 self-start rounded-[16px] bg-nv-bg-page px-6 py-[26px]">
            {data.domain && (
              <div className="flex flex-col gap-[10px]">
                <span className="text-[13px] leading-[21px] text-nv-text-tertiary">적용 산업, 업무 영역</span>
                <span className="text-[15px] leading-6 text-nv-text-primary">{data.domain}</span>
              </div>
            )}
            {data.department && (
              <div className="flex flex-col gap-[10px]">
                <span className="text-[13px] leading-[21px] text-nv-text-tertiary">주 사용 부서</span>
                <span className="text-[15px] leading-6 text-nv-text-primary">{data.department}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </InternalSection>
  );
}

// ── 도입 전후 ──
function StageChip({ children, dark }: { children: string; dark?: boolean }) {
  return (
    <span className={cn("inline-flex h-7 items-center self-start rounded-full px-3 text-[13px] font-bold leading-5", dark ? "bg-nv-brand text-white" : "bg-[#E3E5E8] text-nv-text-secondary")}>{children}</span>
  );
}

function BeforePanel({ before }: { before: NonNullable<InternalBeforeAfter["before"]> }) {
  return (
    <div className="flex flex-col rounded-[20px] bg-nv-bg-page p-8">
      <StageChip>도입 전</StageChip>
      {(before.summary || before.steps.length > 0) && (
        <>
          <h3 className="mt-7 text-[18px] font-medium leading-[29px] text-nv-text-primary">현행 프로세스</h3>
          {before.summary && <p className="mt-[6px] text-[14px] leading-[22px] text-nv-text-secondary">{before.summary}</p>}
        </>
      )}
      {before.steps.length > 0 && (
        <ol className="mt-7 grid grid-cols-2 gap-4 lg:flex lg:items-stretch lg:gap-0">
          {before.steps.map((s, i) => (
            <li key={s.no} className="flex items-center lg:contents">
              <div className="flex min-h-[156px] w-full flex-col rounded-[12px] border border-[#D9DCDF] bg-white p-4 lg:w-[192px] lg:min-w-0">
                <span className="text-[13px] font-medium leading-5 text-nv-text-tertiary">{s.no}</span>
                <span className="mt-[7px] text-[15px] font-medium leading-[22px] text-nv-text-primary">{s.title}</span>
                {s.actor && <span className="mt-[6px] inline-flex h-[21px] items-center self-start rounded-[8px] bg-nv-bg-page px-2 text-[11px] leading-[13px] text-nv-text-secondary">{s.actor}</span>}
                {s.text && <span className="mt-[6px] text-[13px] leading-5 text-nv-text-secondary">{s.text}</span>}
              </div>
              {i < before.steps.length - 1 && (
                <span aria-hidden className="hidden w-7 shrink-0 items-center justify-center text-[18px] leading-none text-nv-text-tertiary lg:flex">
                  →
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      {before.pains.length > 0 && (
        <>
          <h3 className="mt-7 text-[18px] font-medium leading-[29px] text-nv-text-primary">Pain Point</h3>
          <ul className="mt-7 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {before.pains.map((p, i) => (
              <li key={i} className="flex min-h-[114px] flex-col rounded-[12px] border border-nv-border bg-white px-5 py-[18px]">
                <span className="flex items-center gap-[8px] text-[15px] font-medium leading-6 text-nv-text-primary">
                  <span aria-hidden className="h-[6px] w-[6px] shrink-0 rounded-full bg-nv-brand" />
                  {p.title}
                </span>
                {p.text && <span className="mt-2 text-[14px] leading-[22px] text-nv-text-secondary">{p.text}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function AfterPanel({ after }: { after: NonNullable<InternalBeforeAfter["after"]> }) {
  const measured = after.measured;
  return (
    <div className="flex flex-col rounded-[20px] border border-[#F6D3BD] bg-[#FFF9F5] p-8">
      <StageChip dark>도입 후</StageChip>
      <h3 className="mt-6 text-[18px] font-medium leading-[29px] text-nv-text-primary">{measured ? "도입 효과" : "기대 효과"}</h3>
      {after.summary && <p className="mt-[6px] text-[14px] leading-[22px] text-nv-text-secondary">{after.summary}</p>}
      {after.effects.length > 0 && (
        <ul className={cn("mt-6 grid gap-4 md:grid-cols-2", measured ? "items-stretch" : "items-start")}>
          {after.effects.map((e, i) => (
            <li key={i} className={cn("flex flex-col rounded-[12px] border border-[#F6D3BD] bg-white px-[23px] pt-[23px]", measured ? "pb-[33px]" : "pb-[23px]")}>
              <span className="text-[18px] font-bold leading-[30px] text-nv-brand-text">{e.title}</span>
              {e.text && <span className="mt-2 text-[16px] font-medium leading-[26px] text-nv-text-primary">{e.text}</span>}
              {e.note && <span className={cn("text-nv-text-secondary", measured ? "mt-auto pl-4 pt-[42px] text-[13px] leading-[22px]" : "mt-4 text-[14px] leading-[22px]")}>{e.note}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function BeforeAfterSection({ data }: { data: InternalBeforeAfter }) {
  return (
    <InternalSection id="before-after" title="도입 전후" gap={20}>
      <div className="flex flex-col">
        {data.before && <BeforePanel before={data.before} />}
        {data.before && data.after && (
          <div aria-hidden className="flex h-[69px] items-center justify-center text-[26px] leading-none text-nv-brand">
            ↓
          </div>
        )}
        {data.after && <AfterPanel after={data.after} />}
      </div>
    </InternalSection>
  );
}

// ── 차별화 포인트 ──
export function DifferentiatorsSection({ items }: { items: NonNullable<InternalDetailData["differentiators"]> }) {
  const cols = items.length === 4 || (items.length > 4 && items.length % 4 === 0) ? "xl:grid-cols-4" : "xl:grid-cols-3";
  return (
    <InternalSection id="differentiators" title="차별화 포인트" gap={32}>
      <ul className={cn("grid auto-rows-fr gap-4 md:grid-cols-2", cols)}>
        {items.map((d, i) => (
          <li key={i} className="flex flex-col rounded-[16px] border border-nv-border bg-nv-surface p-7">
            <span className="text-[30px] font-normal leading-9 text-nv-brand-text">{String(i + 1).padStart(2, "0")}</span>
            {d.name && <span className="mt-3 text-[17px] font-medium leading-[25px] text-nv-text-primary">{d.name}</span>}
            {d.text && <span className="mt-3 text-[15px] leading-[25px] text-nv-text-secondary">{d.text}</span>}
          </li>
        ))}
      </ul>
    </InternalSection>
  );
}

// ── 핵심 AI 기술 ──
export function TechSection({ data }: { data: InternalTech }) {
  return (
    <InternalSection id="tech" title="핵심 AI 기술">
      {data.items.length > 0 && (
        <ul className="border-b border-nv-border">
          {data.items.map((t) => (
            <li key={t.no} className="grid min-h-[108px] grid-cols-[40px_minmax(0,1fr)] gap-x-2 border-t border-nv-border pb-[23px] pt-[25px] lg:grid-cols-[50px_332px_minmax(0,1fr)] lg:gap-x-0">
              <span className="text-[15px] font-medium leading-7 text-nv-brand-text">{t.no}</span>
              <div className="flex min-w-0 flex-col pr-6">
                <span className="text-[19px] font-medium leading-7 text-nv-text-primary">{t.name}</span>
                {t.kind && <span className="mt-[7px] inline-flex h-6 items-center self-start rounded-[8px] bg-nv-bg-page px-[9px] text-[12px] leading-[18px] text-nv-text-secondary">{t.kind}</span>}
              </div>
              {t.text && <p className="col-span-2 mt-2 text-[16px] leading-[27px] text-nv-text-secondary lg:col-span-1 lg:mt-0">{t.text}</p>}
            </li>
          ))}
        </ul>
      )}
      {data.models.length > 0 && (
        <div className={cn("flex min-h-[69px] flex-wrap items-center gap-x-4 gap-y-2 rounded-[14px] bg-nv-brand-subtle px-7 py-4", data.items.length > 0 && "mt-10")}>
          <span className="flex items-center gap-2 text-[14px] leading-[22px] text-nv-text-secondary">
            사용 AI 모델
            <InternalTag />
          </span>
          {data.models.map((m, i) => (
            <span key={i} className="flex flex-wrap items-baseline gap-4">
              <span className="text-[18px] font-medium leading-[26px] text-nv-text-primary">{m.name}</span>
              {m.serving && <span className="text-[15px] leading-6 text-nv-text-secondary">{m.serving}</span>}
            </span>
          ))}
        </div>
      )}
    </InternalSection>
  );
}

// ── 목표 · 검증 · 성과 지표 ──
function TargetDetail({ metric, stageName }: { metric: InternalMetricItem; stageName: string }) {
  return (
    <div className="flex min-w-0 flex-col pl-2 pt-1">
      <h3 className="text-[20px] font-medium leading-8 text-nv-text-primary">{metric.name}</h3>
      <div className="mt-5 flex items-center gap-6">
        {metric.baseline && (
          <div className="flex min-h-[81px] shrink-0 flex-col justify-center rounded-[12px] border border-nv-border bg-nv-bg-page px-5 py-[14px]">
            <span className="text-[12px] leading-[19px] text-nv-text-tertiary">도입 전</span>
            <span className="mt-1 text-[20px] leading-7 text-nv-text-secondary">{metric.baseline}</span>
          </div>
        )}
        {metric.baseline && metric.target && (
          <span aria-hidden className="text-[28px] leading-[45px] text-nv-text-tertiary">
            →
          </span>
        )}
        {metric.target && (
          <div className="flex min-w-0 flex-col">
            <span className="text-[12px] leading-[19px] text-nv-text-tertiary">목표</span>
            <span className="break-keep text-[44px] font-normal leading-[60px] text-nv-brand-text xl:text-[64px] xl:leading-[74px]">{metric.target}</span>
          </div>
        )}
      </div>
      {metric.basis && <p className="mt-5 text-[15px] leading-[25px] text-nv-text-secondary">{metric.basis}</p>}
      <p className="mt-5 text-[13px] leading-[21px] text-nv-text-tertiary">{stageName} 단계 목표치</p>
    </div>
  );
}

function MeasuredDetail({ metric, valueLabel }: { metric: InternalMetricItem; valueLabel: string }) {
  return (
    <div className="flex min-w-0 flex-col rounded-[16px] bg-nv-bg-page px-9 pb-8 pt-8">
      <h3 className="text-[20px] font-medium leading-8 text-nv-text-primary">{metric.name}</h3>
      <span className="mt-4 text-[13px] font-medium leading-5 text-nv-text-tertiary">{valueLabel}</span>
      <div className="flex flex-wrap items-end gap-x-5">
        <span className="break-keep text-[40px] font-bold leading-[56px] text-nv-brand-text xl:text-[52px] xl:leading-[65px]">{metric.value}</span>
        {metric.target && (
          <span className="text-[14px] font-medium leading-[21px] text-[#6B7280]">
            기준값 {metric.target}
            {metric.met ? " 달성" : ""}
          </span>
        )}
      </div>
      {(metric.basis || metric.scope) && (
        <div className="mt-[14px] grid gap-y-4 border-t border-nv-border pt-[21px] md:grid-cols-2">
          {metric.basis && (
            <div className="flex flex-col gap-1">
              <span className="text-[13px] font-medium leading-5 text-nv-text-tertiary">측정 방법</span>
              <span className="text-[15px] leading-[23px] text-nv-text-primary">{metric.basis}</span>
            </div>
          )}
          {metric.scope && (
            <div className="flex flex-col gap-1">
              <span className="text-[13px] font-medium leading-5 text-nv-text-tertiary">집계 기준</span>
              <span className="text-[15px] leading-[23px] text-nv-text-primary">{metric.scope}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MetricsSection({ data }: { data: InternalMetrics }) {
  const [active, setActive] = useState(0);
  const metric = data.items[Math.min(active, data.items.length - 1)];
  const detail = metric.value ? <MeasuredDetail metric={metric} valueLabel={data.value_label} /> : <TargetDetail metric={metric} stageName={data.stage_name} />;
  return (
    <InternalSection id="metrics" title={data.title}>
      {data.items.length > 1 ? (
        <div className="grid gap-6 lg:grid-cols-[363px_minmax(0,1fr)]">
          <ul className="flex flex-col gap-[10px] self-start">
            {data.items.map((m, i) => (
              <li key={`${m.name}-${i}`}>
                <button
                  type="button"
                  onClick={() => setActive(i)}
                  aria-pressed={i === active}
                  className={cn(
                    "relative flex h-[62px] w-full items-center justify-between gap-3 rounded-[10px] border px-[18px] text-left text-[16px] leading-6",
                    i === active ? "h-[63px] border-[1.5px] border-nv-brand bg-nv-brand-subtle font-medium text-nv-text-primary" : "border-[#D9DCDF] bg-nv-surface text-nv-text-secondary hover:border-nv-border-strong",
                  )}
                >
                  {i === active && <span aria-hidden className="absolute inset-y-[19px] left-[10px] w-[2px] bg-nv-brand" />}
                  <span className={cn(i === active && "pl-3")}>{m.name}</span>
                  {m.value && <span className={cn("shrink-0 leading-6", i === active ? "font-bold text-nv-brand-text" : "font-medium text-nv-text-primary")}>{m.value}</span>}
                </button>
              </li>
            ))}
          </ul>
          {detail}
        </div>
      ) : (
        detail
      )}
    </InternalSection>
  );
}

// ── 보안과 신뢰 ──
export function TrustSection({ data }: { data: NonNullable<InternalDetailData["trust"]> }) {
  return (
    <InternalSection id="trust" title="보안과 신뢰">
      {data.summary && <p className="whitespace-pre-line text-[18px] leading-[30px] text-nv-text-primary xl:text-[22px] xl:leading-[34px]">{data.summary}</p>}
      {data.elements.length > 0 && (
        <ul className={cn("grid gap-4 md:grid-cols-2 lg:grid-cols-3", data.summary && "mt-6")}>
          {data.elements.map((e, i) => (
            <li key={i} className="flex flex-col rounded-[12px] bg-nv-bg-page p-6">
              {e.name && <span className="text-[16px] font-bold leading-6 text-nv-text-primary">{e.name}</span>}
              {e.text && <span className={cn("text-[14px] leading-[22px] text-nv-text-secondary", e.name && "mt-[10px]")}>{e.text}</span>}
            </li>
          ))}
        </ul>
      )}
    </InternalSection>
  );
}
